(() => {
  'use strict';
  const key = 'doi.uploaded-photos.chronological.v4';
  const endpoint = '/apps/my-doi/photos';
  const labels = { photograph: 'Original photography · Mark Miller', enhanced: 'AI-enhanced photograph', assisted: 'AI-assisted image', reconstruction: 'AI reconstruction', illustration: 'AI illustration', generated: 'AI-generated image', unclassified: 'Image · type not recorded' };
  const startup = window.DOIPhotoStartup?.key === key && window.DOIPhotoStartup?.url === endpoint ? window.DOIPhotoStartup : null;
  if (startup) delete window.DOIPhotoStartup;
  let saved = startup?.cached || null;
  let pending = null;
  let early = startup?.request || null;

  function snapshot(data) {
    if (!data || data.source !== 'shopify-files' || data.sort !== 'uploaded-descending' || !Array.isArray(data.items) || data.items.length > 100) return null;
    if (!data.items.every(item => item && typeof item.src === 'string' && Number.isFinite(Date.parse(item.uploadedAt)))) return null;
    return data;
  }

  function cached(current = false) {
    if (!saved) {
      try { saved = JSON.parse(sessionStorage.getItem(key)); } catch (_) {}
    }
    const age = Date.now() - Number(saved?.at);
    return snapshot(saved) && age >= 0 && age < (current ? 60000 : 604800000) ? saved : null;
  }

  function curate(items, editorial, limit = 100) {
    const seen = new Set();
    const clean = [];
    for (const raw of Array.isArray(items) ? items : []) {
      if (!raw || typeof raw.src !== 'string' || !Number.isFinite(Date.parse(raw.uploadedAt))) continue;
      let source;
      try { source = new URL(raw.src, location.origin); } catch (_) { continue; }
      const allowed = source.origin === 'https://cdn.shopify.com' && source.pathname.startsWith('/s/files/1/0868/6813/2121/') || source.origin === location.origin && source.pathname.startsWith('/cdn/shop/');
      if (!allowed) continue;
      const sourceKey = source.pathname.replace(/^\/s\/files\/1\/0868\/6813\/2121/, '/cdn/shop');
      if (seen.has(sourceKey)) continue;
      let filename;
      try { filename = decodeURIComponent(source.pathname.split('/').pop()); } catch (_) { continue; }
      const review = editorial?.entries?.[filename] || {};
      const evidence = `${filename} ${raw.alt || ''}`;
      const detectedAI = /ai[-_ ]?enhanced/i.test(evidence) ? 'enhanced'
        : /ai[-_ ]?(?:assisted|composite)|ai-assisted/i.test(evidence) ? 'assisted'
        : /reconstruction/i.test(evidence) ? 'reconstruction'
        : /illustration|visualization/i.test(evidence) ? 'illustration'
        : /\bai\b|ai[-_ ]|generated/i.test(evidence) ? 'generated' : null;
      const recorded = raw.classificationSource === 'shopify' && labels[raw.classification] ? raw.classification : null;
      const explicit = recorded || (labels[review.classification] ? review.classification : labels[raw.classification] ? raw.classification : null);
      const classification = explicit || detectedAI || (raw.reviewedOriginal ? 'photograph' : 'unclassified');
      let title = review.title || raw.title || '';
      let url = review.url || raw.url;
      let place = review.location || raw.location || 'Coachella Valley';
      let subject = review.subject || raw.subject || url || title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      if (/matt-anderson/i.test(filename) && !review.title) {
        title = 'On court with Matt Anderson'; subject = 'matt-anderson'; place = 'La Quinta'; url = '/products/matt-anderson';
      } else if (/ladera-golf/i.test(filename) && !review.title) {
        title = 'Ladera Golf Club from above'; subject = 'ladera'; place = 'Thermal';
      } else if (/agriculture-thermal/i.test(filename) && !review.title) {
        title = 'The agricultural side of the desert'; subject = 'thermal-fields'; place = 'Thermal';
      } else if (/vulture-thermal/i.test(filename) && !review.title) {
        title = 'Turkey vultures above Thermal'; subject = 'turkey-vulture'; place = 'Thermal';
      } else if (/american-kestrel/i.test(filename) && !review.title) {
        title = 'An American kestrel above the fields'; subject = 'american-kestrel';
      }
      if (!review.title) {
        title = title.replace(/\bMark Miller\b/gi, '').replace(/\b\d{4}[-_]\d{2}[-_]\d{2}\b/g, '').replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();
        if (!title || /\.jpe?g|\.png|\b\d{4}\b/.test(title)) title = String(raw.alt || '').split(/\.\s|;\s/)[0].replace(/,?\s*(?:photo(?:graphed)? by|photographed on|photographed by).*$/i, '').replace(/,?\s*Mark Miller.*$/i, '').trim();
      }
      if (!title) title = 'Recent image';
      if (url && !/^\/(products|blogs|pages|collections)\/[a-z0-9][a-z0-9\-/]*$/i.test(url)) url = null;
      seen.add(sourceKey);
      clean.push({ id: raw.id, src: source.href, title, location: place, url, subject: subject || sourceKey, alt: raw.alt || '', width: raw.width, height: raw.height, uploadedAt: raw.uploadedAt, classificationDetail: recorded ? raw.classificationDetail || '' : review.classificationDetail || raw.classificationDetail || '', classification, classificationLabel: labels[classification], sourceKey, dateLabel: new Date(raw.uploadedAt).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric' }) });
    }
    clean.sort((a, b) => Date.parse(b.uploadedAt) - Date.parse(a.uploadedAt));
    return clean.slice(0, Math.min(100, Math.max(0, limit)));
  }

  function request() {
    if (!pending) {
      const next = early || fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000), headers: { Accept: 'application/json' } }).then(response => response.ok ? response.json() : null).catch(() => null);
      early = null;
      pending = Promise.resolve(next).then(snapshot).finally(() => { pending = null; });
    }
    return pending;
  }

  async function load(root, signal, fresh = false) {
    if (root.dataset.photoEndpoint !== endpoint) return [];
    const abort = () => { if (signal?.aborted) throw new DOMException('Photo request cancelled', 'AbortError'); };
    abort();
    let data = !fresh && cached(true);
    let state = 'current';
    if (!data) {
      data = await request();
      abort();
      if (data) {
        state = data.status === 'current' ? 'current' : 'cached';
        if (state === 'current') {
          saved = { ...data, at: Date.now() };
          try { sessionStorage.setItem(key, JSON.stringify(saved)); } catch (_) {}
        }
      } else {
        data = cached();
        state = 'cached';
      }
    }
    if (!data) throw Error('Photo uploads unavailable');
    root.dataset.photoFeedState = state;
    root.dataset.photoCheckedAt = data.checkedAt || '';
    return data.items;
  }
  window.DOIPhotoDiscovery = { load, curate };
})();
